// =============================================================================
// CAPABILITY STORAGE SERVICE
// =============================================================================
//
// Firebase CRUD operations for Capability Assessments.
//
// =============================================================================

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { CapabilityAssessment, CompleteAssessment } from './types';

const COLLECTION_NAME = 'capabilityAssessments';

// -----------------------------------------------------------------------------
// HELPERS
// -----------------------------------------------------------------------------

/**
 * Remove undefined values from object (Firestore doesn't accept undefined)
 */
function removeUndefined<T extends object>(obj: T): T {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      if (Array.isArray(value)) {
        result[key] = value.map(item => 
          typeof item === 'object' && item !== null 
            ? removeUndefined(item as object)
            : item
        );
      } else if (typeof value === 'object' && value !== null) {
        result[key] = removeUndefined(value as object);
      } else {
        result[key] = value;
      }
    }
  }
  return result as T;
}

// -----------------------------------------------------------------------------
// CREATE
// -----------------------------------------------------------------------------

/**
 * Save completed capability assessment to Firebase
 */
export async function saveAssessment(
  ownerId: string,
  goalId: string,
  assessment: Omit<CapabilityAssessment, 'id' | 'ownerId' | 'goalId' | 'createdAt'>
): Promise<string> {
  const assessmentRef = doc(collection(db, COLLECTION_NAME));
  const now = Date.now();

  const data: CapabilityAssessment = {
    id: assessmentRef.id,
    ownerId,
    goalId,
    ...assessment,
    createdAt: now,
  };

  // Remove undefined values before saving to Firestore
  const cleanData = removeUndefined(data);

  await setDoc(assessmentRef, cleanData);
  return assessmentRef.id;
}

/**
 * Save complete assessment (with DISC, Werkstijl, Extended Skills) to Firebase
 */
export async function saveCompleteAssessment(
  assessment: CompleteAssessment
): Promise<string> {
  const assessmentRef = doc(collection(db, COLLECTION_NAME), assessment.id || doc(collection(db, COLLECTION_NAME)).id);
  
  const data: CompleteAssessment = {
    ...assessment,
    id: assessmentRef.id,
  };

  // Remove undefined values before saving to Firestore
  const cleanData = removeUndefined(data);

  await setDoc(assessmentRef, cleanData);
  return assessmentRef.id;
}

/**
 * Get latest complete assessment for a goal (with DISC, Werkstijl)
 */
export async function getLatestCompleteAssessment(
  ownerId: string,
  goalId: string
): Promise<CompleteAssessment | null> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('goalId', '==', goalId)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }

  // Sort in memory to get the latest
  const assessments = snapshot.docs
    .map(doc => doc.data() as CompleteAssessment)
    .sort((a, b) => b.createdAt - a.createdAt);

  return assessments[0];
}

// -----------------------------------------------------------------------------
// READ
// -----------------------------------------------------------------------------

/**
 * Get latest assessment for a goal
 */
export async function getLatestAssessment(
  ownerId: string,
  goalId: string
): Promise<CapabilityAssessment | null> {
  // Simple query without orderBy to avoid needing composite index
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('goalId', '==', goalId)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }

  // Sort in memory to get the latest
  const assessments = snapshot.docs
    .map(doc => doc.data() as CapabilityAssessment)
    .sort((a, b) => b.createdAt - a.createdAt);

  return assessments[0];
}

/**
 * Check if goal has existing assessment
 */
export async function hasExistingAssessment(
  ownerId: string,
  goalId: string
): Promise<boolean> {
  const assessment = await getLatestAssessment(ownerId, goalId);
  return assessment !== null;
}

/**
 * Get all assessments for owner (across all goals)
 */
export async function getAllAssessments(
  ownerId: string
): Promise<CapabilityAssessment[]> {
  // Simple query without orderBy to avoid needing composite index
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId)
  );

  const snapshot = await getDocs(q);
  
  // Sort in memory
  return snapshot.docs
    .map(doc => doc.data() as CapabilityAssessment)
    .sort((a, b) => b.createdAt - a.createdAt);
}
